import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { SITE } from "@/lib/site";

const links = [
  { href: "#services", label: "Services" },
  { href: "#work", label: "Our Work" },
  { href: "#reviews", label: "Reviews" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isHome = useLocation().pathname === "/";
  // On sub-pages (e.g. /privacy), anchor links must return to the home page first
  const p = isHome ? "" : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHome ? "backdrop-blur-xl bg-black/70 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between py-5">
        <a
          href={`${p}#top`}
          data-testid="brand-logo"
          className="flex items-center gap-3 group"
          aria-label="Everstone Roofing home"
        >
          <span className="relative inline-flex h-9 w-9 items-center justify-center border border-[#D4A84B]">
            <span className="absolute inset-1 border border-[#D4A84B]/40" />
            <span className="font-serif-display text-[#D4A84B] text-lg leading-none">E</span>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-serif-display text-white text-lg tracking-tight">Everstone</span>
            <span className="es-label text-[#D4A84B] mt-1">Roofing · Milwaukee</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10" aria-label="Primary">
          {links.map((l) => (
            <a
              key={l.href}
              href={`${p}${l.href}`}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="es-link text-sm font-medium"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          <a
            href={`tel:${SITE.phone.replace(/[^0-9]/g, "")}`}
            data-testid="nav-phone"
            className="flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors"
          >
            <Phone className="w-4 h-4 text-[#D4A84B]" />
            {SITE.phone}
          </a>
          <a href={`${p}#estimate`} data-testid="nav-cta-estimate" className="es-btn-primary text-sm">
            Get Free Estimate
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          className="md:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={`${p}${l.href}`}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                className="text-white/85 text-base"
              >
                {l.label}
              </a>
            ))}
            <a
              href={`${p}#estimate`}
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-cta"
              className="es-btn-primary text-sm w-fit"
            >
              Get Free Estimate
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
